import debounce from 'lodash/debounce';
import last from 'lodash/last';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';

import {
  AdminJobOrderByOption, Order, PageInfo, GetTeamsFilterInput, TeamOrderByOption, TeamProduct
} from '@clipr/lib';

import { ApiResult, ApiVariables } from '../api/apiSchema';
import { input, InputState } from '../components';
import { SortFieldValue } from '../components/input/sortFieldBtn';
import { assertNotNull, AsyncResult, Maybe, Nullable } from '../core';
import { notifyError } from '../services/notifications';
import { isAgencyAccessRefused } from '../api/apiErrorUtils';
import { BindingProps, StoreNode } from '../store';
import { Store } from '../store/store';
import { SyncStatus } from '../store/syncSchema';
import { Team } from './team';
import { TeamCatalogItem } from './teamCatalogItem';
import { TeamProps } from '.';


export const TeamAvatarSize = {
  width: 100,
  height: 100
}

const TeamProductValues: readonly string[] = Object.values(TeamProduct);

function isTeamProduct(value: unknown): value is TeamProduct {
  return typeof value === 'string' && TeamProductValues.includes(value);
}

export const TeamCoverSize = {
  width: 200
}

export const TeamLogoSize = {
  width: 200
}

type Props = BindingProps<{
  pageSize?: number
}>

export class AdminTeamsCatalogSource
  extends StoreNode {

  readonly nodeType: 'AdminTeamsCatalogSource' = 'AdminTeamsCatalogSource';

  constructor(store: Store, props?: Props) {
    super(store, props);
    makeObservable<this, 'getFetchArgs' | 'fetchRaw'>(this, {
      pageSize: computed,
      syncStatus: observable,
      searchFilter: observable,
      teamProductFilter: observable,
      teamNamespaceFilter: observable,
      organizationIdFilter: observable,
      sortField: observable,
      sortOrder: observable,
      pageInfo: observable,
      lastItemCursor: computed,
      entities: computed,
      isEndOfList: computed,
      setSortFieldValue: action,
      setFilterFieldValue: action,
      fetchTeamNamespaces: action,
      fetch: action,
      fetchMore: action,
      getFetchArgs: action,
      fetchRaw: action,
      handleSearchInputChange: action,
    });

    this.searchBarModel = input(this, {
      name: 'searchInput',
      onChange: (e) => this.handleSearchInputChange(e),
    });
  }

  // #region Resolved props
  // -------
  get pageSize(): number {
    return this.resolvedProps.pageSize;
  }
  // #endregion

  readonly items = observable.array<TeamCatalogItem>();
  readonly teamNamespaces = observable.array<string>();
  readonly searchBarModel: InputState;

  syncStatus: SyncStatus = 'idle';

  searchFilter: string | null = null;
  teamProductFilter: TeamProduct | null = null;
  teamNamespaceFilter: string | null = null;
  organizationIdFilter: string | null = null;

  sortField: TeamOrderByOption | null = 'name';
  sortOrder: Order | null = 'asc';

  pageInfo: PageInfo | null = null;

  get lastItemCursor(): string | null {
    return last(this.items)?.cursor || null;
  }

  get entities(): Team[] {
    return this.items.map(item => item.team);
  }

  get isEndOfList(): boolean {
    return this.pageInfo ? !this.pageInfo.hasNextPage : false;
  }


  getSortFieldValue(field: AdminJobOrderByOption) {
    if (this.sortField !== field)
      return 'none';
    return this.sortOrder!;
  }

  setSortFieldValue(field: TeamOrderByOption, val?: SortFieldValue) {
    if (!val || val === 'none') {
      this.sortField = null;
      this.sortOrder = null;
    } else {
      this.sortField = field;
      this.sortOrder = val;
    }
    this.fetchDebounced();
  }


  getFilterFieldValue(field: keyof GetTeamsFilterInput): Nullable<GetTeamsFilterInput[keyof GetTeamsFilterInput]> {
    switch (field) {
      case 'teamProduct':
        return this.teamProductFilter;

      case 'teamNamespace':
        return this.teamNamespaceFilter;

      case 'organizationId':
        return this.organizationIdFilter;
    }
    return null;
  }

  setFilterFieldValue(field: keyof GetTeamsFilterInput, val?: Maybe<GetTeamsFilterInput[keyof GetTeamsFilterInput]>) {
    switch (field) {
      case 'teamProduct':
        this.teamProductFilter = isTeamProduct(val) ? val : null;
        break;

      case 'teamNamespace':
        this.teamNamespaceFilter = typeof val === 'string' ? val : null;
        break;

      case 'organizationId':
        this.organizationIdFilter = typeof val === 'string' ? val : null;
        break;
    }
    this.fetchDebounced();
  }

  /** Makes a fresh fetch call, clearing any previous data and pagination state. */
  async fetchTeamNamespaces(): AsyncResult<string[]> {

    this.syncStatus = 'fetching';

    const [resp, err] = await this.store.api.getTeamNamespaces();

    if (err)
      return [null, err];
    assertNotNull(resp);

    const retItems: string[] = [];
    runInAction(() => {
      this.teamNamespaces.clear();

      const { getTeamNamespaces } = resp;
      getTeamNamespaces?.forEach(teamNamespace => {
        const { namespace } = teamNamespace!;
        if (namespace)
          this.teamNamespaces.push(namespace);
        retItems.push(namespace);
      });
    });

    return [retItems];
  }

  fetchDebounced = debounce(() => this.fetch(), 500);

  /** Makes a fresh fetch call, clearing any previous data and pagination state. */
  async fetch()
    : AsyncResult<TeamCatalogItem[]> {

    this.syncStatus = 'fetching';

    const [teamsRes, err] = await this.fetchRaw();

    if (err)
      return [null, err];
    assertNotNull(teamsRes);

    const retItems: TeamCatalogItem[] = [];
    runInAction(() => {
      this.items.clear();

      const { getTeams } = teamsRes;
      this.pageInfo = getTeams.pageInfo;

      getTeams.edges.forEach((edge, i) => {

        this.store.teamManager.insertAdminTeam(edge.node as TeamProps);

        const item = new TeamCatalogItem(this.store, {
          teamId: edge.node.id,
          cursor: edge.cursor,
          index: i
        });

        this.items.push(item);
        retItems.push(item);
      });
    });

    return [retItems];
  }

  async fetchMore()
    : AsyncResult<TeamCatalogItem[]> {

    this.syncStatus = 'fetchingMore';

    const [jobsRes, err] = await this.fetchRaw(true);
    if (err)
      return [null, err];
    assertNotNull(jobsRes);

    const retItems: TeamCatalogItem[] = [];
    runInAction(() => {
      const { getTeams } = jobsRes;
      this.pageInfo = getTeams.pageInfo;

      getTeams.edges.forEach((edge, i) => {

        this.store.teamManager.insertAdminTeam(edge.node as TeamProps);

        const item = new TeamCatalogItem(this.store, {
          teamId: edge.node.id,
          cursor: edge.cursor,
          index: i
        });

        this.items.push(item);
        retItems.push(item);
      });
    });

    return [retItems];
  }

  private getFetchArgs(more: boolean = false) {
    const args: ApiVariables<'getTeams'> = {
      coverSize: [TeamCoverSize],
      logoSize: [TeamLogoSize],
      avatarSize: [TeamAvatarSize],
    };
    const filter: GetTeamsFilterInput = {
      scope: 'Admin'
    };

    if (this.sortField && this.sortOrder) {
      args.sort = {
        field: this.sortField,
        order: this.sortOrder
      }
    }

    if (this.teamProductFilter) {
      filter.teamProduct = this.teamProductFilter;
    }

    if (this.teamNamespaceFilter) {
      filter.teamNamespace = this.teamNamespaceFilter === 'None' ? '' : this.teamNamespaceFilter!;
    }

    if (this.organizationIdFilter) {
      filter.organizationId = this.organizationIdFilter;
    }

    if (this.searchFilter) {
      filter._search = this.searchFilter || undefined;
    }

    if (Object.keys(filter).length > 0)
      args.filter = filter;

    args.first = this.pageSize;

    if (more && this.lastItemCursor)
      args.after = this.lastItemCursor;

    return args;
  }

  private async fetchRaw(more: boolean = false): ApiResult<'getTeams'> {
    const args: ApiVariables<'getTeams'> = this.getFetchArgs(more);

    const [teamsRes, err] = await this.store.api.getTeams(args);
    if (err) {
      runInAction(() => {
        this.syncStatus = 'error';
        // A refused agency request renders as an empty state; the page refreshes access.
        if (!isAgencyAccessRefused(err))
          notifyError(this, `Could not fetch teams because of an error.`);
      });
      return [null, err];
    }

    runInAction(() => {
      this.syncStatus = 'idle';
    });

    assertNotNull(teamsRes);
    return [teamsRes];
  }


  handleSearchInputChange(evt: InputState) {
    this.searchFilter = evt.value || null;
    this.fetchDebounced();
  }
}
